import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { X, ChevronDown, Power, PowerOff, Layers, Trash2, Loader2 } from "lucide-react";
import { useBulkUpdateProducts, useBulkDeleteProducts } from "@/hooks/useProducts";

const categories = ["Software", "Hardware", "Service", "Subscription", "Consulting", "Training", "Support"];

interface BulkProductActionsProps {
  selectedIds: string[];
  onClearSelection: () => void;
}

export function BulkProductActions({ selectedIds, onClearSelection }: BulkProductActionsProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const bulkUpdate = useBulkUpdateProducts();
  const bulkDelete = useBulkDeleteProducts();

  const handleActivate = async () => {
    await bulkUpdate.mutateAsync({ ids: selectedIds, updates: { is_active: true } });
    onClearSelection();
  };

  const handleDeactivate = async () => {
    await bulkUpdate.mutateAsync({ ids: selectedIds, updates: { is_active: false } });
    onClearSelection();
  };

  const handleSetCategory = async (category: string) => {
    await bulkUpdate.mutateAsync({ ids: selectedIds, updates: { category } });
    onClearSelection();
  };

  const handleDelete = async () => {
    await bulkDelete.mutateAsync(selectedIds);
    setShowDeleteDialog(false);
    onClearSelection();
  };

  const isLoading = bulkUpdate.isPending || bulkDelete.isPending;

  return (
    <>
      <div className="flex items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg animate-in fade-in slide-in-from-top-2">
        <Badge variant="secondary" className="font-medium">
          {selectedIds.length} selected
        </Badge>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleActivate}
            disabled={isLoading}
          >
            {bulkUpdate.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Power className="h-4 w-4 mr-1" />
            )}
            Activate
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={handleDeactivate}
            disabled={isLoading}
          >
            <PowerOff className="h-4 w-4 mr-1" />
            Deactivate
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={isLoading}>
                <Layers className="h-4 w-4 mr-1" />
                Set Category
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {categories.map((category) => (
                <DropdownMenuItem key={category} onClick={() => handleSetCategory(category)}>
                  {category}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleSetCategory("")}>
                Clear Category
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isLoading}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>

        <Button
          size="sm"
          variant="ghost"
          onClick={onClearSelection}
          className="ml-auto"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.length} Products</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} products? This will also
              delete all associated pricing rules. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              {bulkDelete.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete All"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
